import React, { useContext } from 'react'
import Box from '@material-ui/core/Box'

import { CashflowEventsContext } from '../../context'
import messages from '../../messages.json'
import { EVENT_TYPES } from '../../reducer'
import { Formik, Form } from 'formik'
import { InputFieldWithLabel, NumberFieldWithLabel, CheckboxWithLabel, Select } from '../../../../components/Form/Fields'
import { formValidationSchema } from '../../helpers/validations'
import { Button } from '../../../../components/Button'
import { Hider } from '../../../../components/HOCs/Hider'
import { EventMessages } from '../../Shared'
import { getFormEventTypeFromState } from '../../helpers/stateHelper'
import { onSubmit, frequencyOptions } from '../../helpers/formHelper'
import { getYearFromAge } from '../../../../utils/dateParser'
import { annualInflationValueLabel, annualInflationCheckboxLabel } from './labels'

export const IndividualEventForm = () => {
  const context = useContext(CashflowEventsContext)
  const { state, actions } = context
  const { eventAttributes, isFormValid, userDetails, eventType, assumptions, loading } = state
  const { back, formDelete } = actions
  const { clientCurrentAge } = userDetails
  const { inflation } = assumptions

  const deleteLabel = getDeleteLabelFromEventType(eventAttributes)
  const sourceLabel = getSourceLabelFromEventType()
  return (
    <Formik
      onSubmit={onSubmit()}
      initialValues={initialValues(eventAttributes)}
      validationSchema={formValidationSchema()}
      render={({ isValid, values, errors }) => (
        <Form>
          <InputFieldWithLabel name='name' label={sourceLabel} />
          <NumberFieldWithLabel name='amount' label='Amount' />
          <EventAgeFields errors={errors} values={values} clientCurrentAge={clientCurrentAge} />
          <AnnualInflationFields errors={errors} values={values} eventType={eventType} inflation={inflation} />
          <EventMessages show={!isFormValid} />

          <Box className='grid grid-cols-2 gap-4'>
            <Button className='button button--secondary' onClick={back}>Back</Button>
            <Button className='button button--primary' type='submit' disabled={!isValid || loading}>
              {loading ? 'Loading...' : 'Add to plan'}
            </Button>
            <Button disabled={loading} className='button button--tertiary col-span-2' onClick={formDelete(eventAttributes, context)}>
              {deleteLabel}
            </Button>
          </Box>
        </Form>
      )} />
  )
}

const AnnualInflationFields = ({ errors, values, eventType, inflation }) => {
  const checkboxLabel = annualInflationCheckboxLabel[eventType]
  const valueLabel = annualInflationValueLabel[eventType]
  return (
    <div className="form__annual-inflation-fields">
      <Box mb='1rem'>
        <Hider withTransition collapse
          show={values.apply_annual_inflation}
          render={() =>
            <NumberFieldWithLabel name='annual_inflation' label={valueLabel} />
          } />
        <CheckboxWithLabel name='apply_annual_inflation' label={checkboxLabel} />
        {!values.apply_annual_inflation &&
          <DefaultInflationMessage eventType={eventType} inflation={inflation} />
        }
      </Box>
    </div>
  )
}

const DefaultInflationMessage = ({ eventType, inflation }) => {
  return (
    <Box ml='1rem'>
      <p>
        By default, an annual {eventType === EVENT_TYPES.INCOME ? 'increase ' : 'inflation '}
        of <strong>{eventType === EVENT_TYPES.INCOME ? 0 : inflation * 100}%</strong> is applied to this {eventType === EVENT_TYPES.INCOME ? 'income' : 'expense'}
      </p>
    </Box>
  )
}


const EventAgeFields = ({ errors, values, clientCurrentAge }) =>
  <div className='form__age-fields'>
    <NumberFieldWithLabel name='start_age'
      label={getStartAgeLabel(values.recurring)}
      subtext={yearSubtext(errors.start_age, values.start_age, clientCurrentAge)} />
    <Hider withTransition collapse
      show={values.recurring}
      render={() =>
        <NumberFieldWithLabel name='end_age' label={messages.form.recurringEndAge} subtext={yearSubtext(errors.end_age, values.end_age, clientCurrentAge)} />
      } />
    <Box display='flex'>
      <CheckboxWithLabel name='recurring' label='Recurring Event' />
      <Hider withTransition collapse
        show={showFrequencyField(errors, values)}
        render={() =>
          <Select name='frequency' options={frequencyOptions(values, errors)} />
        } />
    </Box>
  </div>

const yearSubtext = (error, age, clientCurrentAge) => {
  const eventYear = getYearFromAge(age, clientCurrentAge)
  if (!error && !isNaN(eventYear)) {
    return `Event year: ${eventYear}`
  } else {
    return null
  }
}

const showFrequencyField = (errors, values) =>
  !(errors.end_age || errors.start_age) && values.recurring

const initialValues = (eventAttributes) => ({
  id: eventAttributes ? eventAttributes.id : null,
  name: eventAttributes ? eventAttributes.name : '',
  amount: eventAttributes ? eventAttributes.amount : '',
  start_age: eventAttributes ? eventAttributes.start_age : '',
  recurring: eventAttributes ? (eventAttributes.end_age !== eventAttributes.start_age) : false,
  end_age: eventAttributes ? eventAttributes.end_age : '',
  frequency: eventAttributes ? eventAttributes.frequency : 1,
  apply_annual_inflation: eventAttributes ? (eventAttributes.annual_inflation !== null) : false,
  annual_inflation: eventAttributes ? eventAttributes.annual_inflation * 100 : '',
  status: eventAttributes ? eventAttributes.status : 'active',
})

const getDeleteLabelFromEventType = (eventAttributes) => {
  const formEventType = getFormEventTypeFromState()
  if (eventAttributes) {
    return formEventType === EVENT_TYPES.INCOME
      ? messages.form.deleteIncomeEvent
      : messages.form.deleteExpenseEvent
  } else {
    return messages.form.backToCashflowEvents
  }
}

const getSourceLabelFromEventType = () => {
  const formEventType = getFormEventTypeFromState()
  return formEventType === EVENT_TYPES.INCOME
    ? messages.form.sourceLabelIncomeEvent
    : messages.form.sourceLabelExpenseEvent
}

const getStartAgeLabel = (recurring) => {
  return recurring
    ? messages.form.recurringStartAge
    : messages.form.nonRecurringStartAge
}
